import {
  layoutToFormState,
  parsePriceFromPerson,
  pickAreaTablesFromApi,
  pickOptionalSoldOutFlag,
  toSoldOutFlag,
} from "../pages/tableBooking/tableBookingUtils";

export const pricingOverrideKey = (areaId, tableId) => {
  const aid = String(areaId ?? "").trim();
  const tid = tableId != null ? String(tableId).trim() : "";
  return tid ? `${aid}:${tid}` : aid;
};

export const MANUAL_AVAILABILITY_VALUES = ["AUTO", "SOLD_OUT", "UNAVAILABLE"];

/** Coerce any manualAvailability alias to AUTO | SOLD_OUT | UNAVAILABLE. */
export const normalizeManualAvailability = (value) => {
  const key = String(value ?? "").trim().toUpperCase();
  return MANUAL_AVAILABILITY_VALUES.includes(key) ? key : "AUTO";
};

/** Optional positive integer party size; blank/invalid -> undefined. */
export const toOptionalPartySize = (value) => {
  if (value === "" || value == null) return undefined;
  const n = Math.trunc(Number(value));
  if (!Number.isFinite(n) || n < 1) return undefined;
  return n;
};

const firstDefined = (...values) =>
  values.find((v) => v !== undefined && v !== null && v !== "");

/**
 * True when an (event) override row actually changes something vs. the layout.
 * Accepts raw form rows ("" allowed) and API rows. Currency-only is ignored
 * because currency is meaningless without a price override.
 */
export const rowHasOverride = (row) => {
  if (!row) return false;
  const price = parsePriceFromPerson(
    row.priceFromPerPerson ?? row.price_from_per_person
  );
  const hasLabel = Boolean(
    String(row.priceDisplayLabel ?? row.price_display_label ?? "").trim()
  );
  const hasMin =
    toOptionalPartySize(
      firstDefined(row.minPartySize, row.minpartysize, row.min_party_size)
    ) != null;
  const hasMax =
    toOptionalPartySize(
      firstDefined(row.maxPartySize, row.maxpartysize, row.max_party_size)
    ) != null;
  const hasDesc = Boolean(String(row.description ?? "").trim());
  const hasManual =
    normalizeManualAvailability(
      firstDefined(
        row.manualAvailability,
        row.manualavailability,
        row.manual_availability
      )
    ) !== "AUTO";
  const hasOnlineSoldOut =
    pickOptionalSoldOutFlag(row, "onlineSoldOut", "online_sold_out") !==
    undefined;
  const hasOfflineSoldOut =
    pickOptionalSoldOutFlag(row, "offlineSoldOut", "offline_sold_out") !==
    undefined;
  return (
    price > 0 ||
    hasLabel ||
    hasMin ||
    hasMax ||
    hasDesc ||
    hasManual ||
    hasOnlineSoldOut ||
    hasOfflineSoldOut
  );
};

export const indexAreaPricingRows = (rows) => {
  const map = new Map();
  for (const row of rows || []) {
    const areaId = row?.areaId ?? row?.area_id;
    if (areaId == null || areaId === "") continue;
    const tableId = row?.tableId ?? row?.table_id;
    map.set(pricingOverrideKey(areaId, tableId), row);
  }
  return map;
};

export const pickEventTableBookingFromItem = (item) => {
  const nested = item?.tableBooking ?? item?.table_booking ?? {};
  const enabled = Boolean(
    item?.tableBookingEnabled ??
      item?.table_booking_enabled ??
      nested?.tableBookingEnabled ??
      nested?.enabled
  );
  const layoutId = String(
    item?.tableBookingLayoutId ??
      item?.table_booking_layout_id ??
      nested?.layoutId ??
      nested?.layout_id ??
      ""
  ).trim();
  const viewkey = String(
    item?.tableBookingViewkey ??
      item?.table_booking_viewkey ??
      nested?.viewkey ??
      nested?.viewKey ??
      ""
  )
    .trim()
    .toLowerCase();
  const slotKey = String(
    item?.tableBookingSlotKey ??
      item?.table_booking_slot_key ??
      nested?.slotKey ??
      nested?.slot_key ??
      ""
  ).trim();
  const areaPricing =
    item?.tableBookingAreaPricing ??
    item?.table_booking_area_pricing ??
    nested?.areaPricing ??
    nested?.area_pricing ??
    [];

  return {
    tableBookingEnabled: enabled,
    tableBookingLayoutId: layoutId,
    tableBookingViewkey: viewkey,
    tableBookingSlotKey: slotKey,
    tableBookingAreaPricing: Array.isArray(areaPricing)
      ? areaPricing.map(normalizeAreaPricingRow)
      : [],
  };
};

/**
 * Clean API-ready pricing row per the event contract. Accepts camelCase and
 * snake_case aliases. Only areaId + priceFromPerPerson are guaranteed; the rest
 * are emitted only when set so unset fields keep the layout value server-side.
 */
export const normalizeAreaPricingRow = (row) => {
  const areaId = String(row?.areaId ?? row?.area_id ?? row?.areaid ?? "").trim();
  const tableRaw = firstDefined(row?.tableId, row?.table_id, row?.tableid);
  const tableId = tableRaw != null ? String(tableRaw).trim() : "";

  const out = { areaId };
  if (tableId) out.tableId = tableId;

  out.priceFromPerPerson = parsePriceFromPerson(
    row?.priceFromPerPerson ?? row?.price_from_per_person
  );
  out.currency = String(row?.currency ?? "INR").trim() || "INR";

  const label = String(
    row?.priceDisplayLabel ?? row?.price_display_label ?? ""
  ).trim();
  if (label) out.priceDisplayLabel = label;

  const minP = toOptionalPartySize(
    firstDefined(row?.minPartySize, row?.minpartysize, row?.min_party_size)
  );
  if (minP != null) out.minPartySize = minP;

  const maxP = toOptionalPartySize(
    firstDefined(row?.maxPartySize, row?.maxpartysize, row?.max_party_size)
  );
  if (maxP != null) out.maxPartySize = maxP;

  const description = String(row?.description ?? "").trim();
  if (description) out.description = description;

  const manualAvailability = normalizeManualAvailability(
    firstDefined(
      row?.manualAvailability,
      row?.manualavailability,
      row?.manual_availability
    )
  );
  if (manualAvailability !== "AUTO") out.manualAvailability = manualAvailability;

  const onlineSoldOut = pickOptionalSoldOutFlag(
    row,
    "onlineSoldOut",
    "online_sold_out"
  );
  if (onlineSoldOut !== undefined) out.onlineSoldOut = onlineSoldOut;

  const offlineSoldOut = pickOptionalSoldOutFlag(
    row,
    "offlineSoldOut",
    "offline_sold_out"
  );
  if (offlineSoldOut !== undefined) out.offlineSoldOut = offlineSoldOut;

  return out;
};

export const upsertAreaPricingRow = (rows, patch) => {
  const next = [...(rows || [])];
  const areaId = String(patch?.areaId ?? "").trim();
  if (!areaId) return next;
  const tableId =
    patch?.tableId != null && patch?.tableId !== ""
      ? String(patch.tableId).trim()
      : undefined;
  const key = pricingOverrideKey(areaId, tableId);
  const idx = next.findIndex(
    (r) => pricingOverrideKey(r?.areaId, r?.tableId) === key
  );
  const row = normalizeAreaPricingRow({ ...patch, areaId, tableId });
  if (idx >= 0) next[idx] = row;
  else next.push(row);
  return next;
};

export const removeAreaPricingRow = (rows, areaId, tableId) => {
  const key = pricingOverrideKey(areaId, tableId);
  return (rows || []).filter(
    (r) => pricingOverrideKey(r?.areaId, r?.tableId) !== key
  );
};

/**
 * Build the tableBookingAreaPricing payload: keep only rows that actually
 * override a layout value, de-dupe by areaId(:tableId) keeping the first, and
 * guarantee the contract-required priceFromPerPerson (falling back to the
 * captured layout default when the admin only changed availability/party size).
 */
export const buildTableBookingAreaPricingPayload = (rows) => {
  const seen = new Set();
  const out = [];
  for (const raw of rows || []) {
    const areaId = String(raw?.areaId ?? raw?.area_id ?? raw?.areaid ?? "").trim();
    if (!areaId) continue;
    if (!rowHasOverride(raw)) continue;

    const row = normalizeAreaPricingRow(raw);
    const key = pricingOverrideKey(row.areaId, row.tableId);
    if (seen.has(key)) continue;
    seen.add(key);

    if (!(row.priceFromPerPerson > 0)) {
      const fallback = parsePriceFromPerson(raw?.layoutPrice);
      row.priceFromPerPerson = fallback > 0 ? fallback : 0;
    }
    out.push(row);
  }
  return out;
};

export const buildTableBookingEventPayload = (formValues) => {
  if (!formValues?.tableBookingEnabled) {
    return {
      tableBookingEnabled: false,
      tableBookingAreaPricing: [],
    };
  }

  const layoutId = String(formValues?.tableBookingLayoutId ?? "").trim();
  const viewkey = String(formValues?.tableBookingViewkey ?? "")
    .trim()
    .toLowerCase();
  const slotKey = String(formValues?.tableBookingSlotKey ?? "").trim();

  return {
    tableBookingEnabled: true,
    tableBookingLayoutId: layoutId,
    tableBookingViewkey: viewkey,
    tableBookingSlotKey: slotKey,
    tableBookingAreaPricing: buildTableBookingAreaPricingPayload(
      formValues?.tableBookingAreaPricing
    ),
  };
};

export const pickAreaOrTableId = (row) => {
  const raw = row?._id ?? row?.id;
  if (raw == null || raw === "") return "";
  if (typeof raw === "object") {
    if (raw.$oid) return String(raw.$oid).trim();
    if (raw.toString && raw.toString() !== "[object Object]") {
      return String(raw.toString()).trim();
    }
    return "";
  }
  return String(raw).trim();
};

const extractRawPriceValue = (row) => {
  if (!row || typeof row !== "object") return null;
  const candidates = [
    row.priceFromPerPerson,
    row.price_from_per_person,
    row.pricePerPerson,
    row.price_per_person,
    row.price,
    row.minimumSpend,
    row.minimum_spend,
  ];
  for (const raw of candidates) {
    if (raw == null || raw === "") continue;
    if (typeof raw === "object") {
      if (raw.$numberDecimal != null) return raw.$numberDecimal;
      if (raw.$oid) continue;
    }
    return raw;
  }
  return null;
};

export const normalizeLayoutForEventPricing = (layout) => {
  if (!layout) return null;
  try {
    const form = layoutToFormState(layout);
    return {
      ...layout,
      areas: form.areas,
    };
  } catch {
    return layout;
  }
};

export const pickLayoutDefaultPrice = (row) => {
  const price = parsePriceFromPerson(extractRawPriceValue(row));
  const currency = String(row?.currency ?? row?.currencyCode ?? "INR").trim() || "INR";
  const label = String(
    row?.priceDisplayLabel ?? row?.price_display_label ?? ""
  ).trim();
  const minPartySize = toOptionalPartySize(
    firstDefined(row?.minPartySize, row?.min_party_size)
  );
  const maxPartySize = toOptionalPartySize(
    firstDefined(row?.maxPartySize, row?.max_party_size)
  );
  const manualAvailability = normalizeManualAvailability(
    firstDefined(row?.manualAvailability, row?.manual_availability)
  );
  const description = String(row?.description ?? "").trim();
  return {
    price,
    currency,
    label,
    minPartySize,
    maxPartySize,
    manualAvailability,
    description,
    onlineSoldOut: toSoldOutFlag(row?.onlineSoldOut ?? row?.online_sold_out),
    offlineSoldOut: toSoldOutFlag(row?.offlineSoldOut ?? row?.offline_sold_out),
  };
};

/** Table rows inherit zone defaults when the table has no own value (layout editor behavior). */
export const resolveLayoutDefaultPrice = (row, zoneRow = null) => {
  const own = pickLayoutDefaultPrice(row);
  const zone = zoneRow ? pickLayoutDefaultPrice(zoneRow) : null;

  let { price, currency, label } = own;
  let inherited = false;
  if (!(own.price > 0 || own.label) && zone && (zone.price > 0 || zone.label)) {
    price = zone.price;
    currency = zone.currency;
    label = zone.label;
    inherited = true;
  }

  return {
    price,
    currency,
    label,
    inherited,
    minPartySize: own.minPartySize ?? zone?.minPartySize,
    maxPartySize: own.maxPartySize ?? zone?.maxPartySize,
    manualAvailability:
      own.manualAvailability && own.manualAvailability !== "AUTO"
        ? own.manualAvailability
        : zone?.manualAvailability ?? "AUTO",
    description: own.description || zone?.description || "",
    onlineSoldOut: Boolean(own.onlineSoldOut || zone?.onlineSoldOut),
    offlineSoldOut: Boolean(own.offlineSoldOut || zone?.offlineSoldOut),
    onlineSoldOutInherited: Boolean(!own.onlineSoldOut && zone?.onlineSoldOut),
    offlineSoldOutInherited: Boolean(
      !own.offlineSoldOut && zone?.offlineSoldOut
    ),
  };
};

export const formatDefaultPriceHint = (layoutDefault, fallbackCurrency = "INR") => {
  if (!layoutDefault) return "—";
  const price =
    layoutDefault?.price != null
      ? Number(layoutDefault.price)
      : parsePriceFromPerson(extractRawPriceValue(layoutDefault));
  const currency =
    String(layoutDefault?.currency ?? fallbackCurrency).trim() || fallbackCurrency;
  const label = String(layoutDefault?.label ?? "").trim();
  if (label) return label;
  if (price > 0) {
    const formatted = price.toLocaleString("en-IN");
    if (currency === "INR") return `INR ${formatted} / person`;
    return `${currency} ${formatted} / person`;
  }
  return "Not set in layout";
};

const parseOverrideEventPrice = (override) => {
  if (!override) return { price: 0, currency: null };
  const price = parsePriceFromPerson(override.priceFromPerPerson);
  const currency = String(override?.currency ?? "").trim() || null;
  return { price, currency };
};

/**
 * Effective event price for a zone/table row: table override → zone override → layout default.
 * Used for pills and placeholders so nested tables inherit the zone event price.
 */
export const resolveEffectiveEventPricing = (row, overrideIndex) => {
  const layoutDefault = row?.layoutDefault || {};
  const layoutPrice = Number(layoutDefault.price) || 0;
  const layoutCurrency = layoutDefault.currency || "INR";

  const zoneOverride = overrideIndex?.get?.(row.areaId);
  const zoneParsed = parseOverrideEventPrice(zoneOverride);

  if (row?.kind === "zone" || !row?.tableId) {
    if (zoneParsed.price > 0) {
      return {
        price: zoneParsed.price,
        currency: zoneParsed.currency || layoutCurrency,
        source: "zone",
      };
    }
    return { price: layoutPrice, currency: layoutCurrency, source: "layout" };
  }

  const tableOverride = overrideIndex?.get?.(
    pricingOverrideKey(row.areaId, row.tableId)
  );
  const tableParsed = parseOverrideEventPrice(tableOverride);

  if (tableParsed.price > 0) {
    return {
      price: tableParsed.price,
      currency:
        tableParsed.currency || zoneParsed.currency || layoutCurrency,
      source: "table",
    };
  }
  if (zoneParsed.price > 0) {
    return {
      price: zoneParsed.price,
      currency: zoneParsed.currency || layoutCurrency,
      source: "zone",
    };
  }
  return { price: layoutPrice, currency: layoutCurrency, source: "layout" };
};

const channelSoldOutSnake = (field) =>
  field === "offlineSoldOut" ? "offline_sold_out" : "online_sold_out";

/**
 * Effective onlineSoldOut / offlineSoldOut for an event pricing row.
 * Explicit event override (true or false) wins; omitted uses layout.
 * Nested tables inherit a zone event override of true.
 */
export const resolveEventChannelSoldOut = (row, overrideIndex, field) => {
  const snake = channelSoldOutSnake(field);
  const layoutVal = Boolean(row?.layoutDefault?.[field]);
  const zoneOverride = overrideIndex?.get?.(row.areaId);
  const zoneFlag = pickOptionalSoldOutFlag(zoneOverride, field, snake);

  if (row?.tableId) {
    const tableOverride = overrideIndex?.get?.(
      pricingOverrideKey(row.areaId, row.tableId)
    );
    const tableFlag = pickOptionalSoldOutFlag(tableOverride, field, snake);
    if (tableFlag !== undefined) {
      return { checked: tableFlag, inherited: false, source: "table" };
    }
    if (zoneFlag === true) {
      return { checked: true, inherited: true, source: "zone" };
    }
    if (zoneFlag === false) {
      return { checked: false, inherited: false, source: "zone" };
    }
    return {
      checked: layoutVal,
      inherited: Boolean(row?.layoutDefault?.[`${field}Inherited`]),
      source: "layout",
    };
  }

  if (zoneFlag !== undefined) {
    return { checked: zoneFlag, inherited: false, source: "zone" };
  }
  return { checked: layoutVal, inherited: false, source: "layout" };
};

export const formatEffectivePriceHint = (effective, layoutDefault) => {
  const label = String(layoutDefault?.label ?? "").trim();
  if (effective?.source === "layout" && label) return label;
  if (effective?.price > 0) {
    const formatted = effective.price.toLocaleString("en-IN");
    const currency = effective.currency || "INR";
    if (currency === "INR") return `INR ${formatted} / person`;
    return `${currency} ${formatted} / person`;
  }
  return formatDefaultPriceHint(layoutDefault);
};

export const flattenLayoutPricingRows = (layout) => {
  const normalized = normalizeLayoutForEventPricing(layout) ?? layout;
  const areas = Array.isArray(normalized?.areas) ? normalized.areas : [];
  const rows = [];
  for (const area of areas) {
    const areaId = pickAreaOrTableId(area);
    if (!areaId) continue;
    const zoneDefault = resolveLayoutDefaultPrice(area);
    rows.push({
      kind: "zone",
      areaId,
      label: String(area?.label ?? area?.name ?? "Zone"),
      layoutDefault: zoneDefault,
    });
    for (const table of pickAreaTablesFromApi(area)) {
      const tableId = pickAreaOrTableId(table);
      if (!tableId) continue;
      rows.push({
        kind: "table",
        areaId,
        tableId,
        label: String(table?.label ?? table?.name ?? "Table"),
        zoneLabel: String(area?.label ?? area?.name ?? ""),
        layoutDefault: resolveLayoutDefaultPrice(table, area),
      });
    }
  }
  return rows;
};

export const formatLayoutPricePlaceholder = (layoutDefault, t) => {
  const price = Number(layoutDefault?.price) || 0;
  if (price > 0) return price.toLocaleString("en-IN");
  return t("EVENT_TABLE_BOOKING_INHERIT_LAYOUT");
};

export const formatEffectivePricePlaceholder = (effective, layoutDefault, t) => {
  if (effective?.price > 0) {
    return effective.price.toLocaleString("en-IN");
  }
  return formatLayoutPricePlaceholder(layoutDefault, t);
};

export const formatLayoutDisplayLabelPlaceholder = (layoutDefault) => {
  const price = Number(layoutDefault?.price) || 0;
  const currency = layoutDefault?.currency || "INR";
  const label = String(layoutDefault?.label ?? "").trim();
  if (label) return `e.g. ${label}`;
  if (price > 0) {
    return `e.g. From ${currency === "INR" ? "₹" : `${currency} `}${price.toLocaleString("en-IN")} / person`;
  }
  return "e.g. From ₹5,000 / person";
};

export const validateEventTableBooking = (formValues, t) => {
  if (!formValues?.tableBookingEnabled) return null;
  if (!String(formValues?.tableBookingLayoutId ?? "").trim()) {
    return t("EVENT_TABLE_BOOKING_ERR_LAYOUT");
  }
  if (!String(formValues?.tableBookingViewkey ?? "").trim()) {
    return t("EVENT_TABLE_BOOKING_ERR_VIEWKEY");
  }
  if (!String(formValues?.tableBookingSlotKey ?? "").trim()) {
    return t("EVENT_TABLE_BOOKING_ERR_SLOT");
  }

  for (const raw of formValues?.tableBookingAreaPricing || []) {
    const row = normalizeAreaPricingRow(raw);
    if (
      row.minPartySize != null &&
      row.maxPartySize != null &&
      row.minPartySize > row.maxPartySize
    ) {
      return t("EVENT_TABLE_BOOKING_ERR_PARTY_SIZE");
    }
  }
  return null;
};
